import { IPaginationReturn } from '../../interfaces/IPaginationReturn';
import { useRequest } from '../providers/useRequest';

import AppError from '../../errors/AppError';
import ICompany from '../../interfaces/ICompany';

export interface IFilterCompany {
  id?: number | null;
}

const useCompanyService = () => {
  const company = useRequest<ICompany>();
  const path = 'employee/company';

  const list = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    direction: 'asc' | 'desc',
    filter?: IFilterCompany,
  ): Promise<IPaginationReturn<ICompany[]>> => {
    return await company
      .postAndGetManyPaginated({
        path: `${path}/list?perPage=${perPage}&currentPage=${currentPage}&orderBy=${orderField}&orderDirection=${direction}`,
        sendAuthorization: true,
        body: { ...filter },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const findById = async (id: number): Promise<ICompany> => {
    return await company
      .getOne({
        path: `${path}/${id}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listCompanyByName = async (name: string): Promise<ICompany[]> => {
    return await company
      .getMany({
        path: `${path}/findByName/${name}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listByEmployeeId = async (employeeId: number): Promise<ICompany> => {
    return await company
      .getOne({
        path: `${path}/findByEmployeeId/${employeeId}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listCompanySearch = async (filter: string): Promise<ICompany[]> => {
    if (filter === '') {
      filter = ' ';
    }

    const url = filter
      ? `${path}/search?perPage=300&currentPage=1&orderBy=code&orderDirection=asc&filter=${filter}`
      : `${path}/search?perPage=300&currentPage=1&orderBy=code&orderDirection=asc&filter= `;

    return await company
      .getManyPaginated({
        path: url,
        sendAuthorization: true,
      })
      .then(result => {
        return result?.data;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const create = async ({
    code,
    name,
    sendNotificationsWhenCreated,
    sendNotificationsWhenApproved,
    sendNotificationsWhenDisapproved,
    notificationEmails,
    allowJustificationWhenApprovingTheDocument,
  }: ICompany): Promise<ICompany> => {
    return await company
      .post({
        path: `${path}`,
        sendAuthorization: true,
        body: {
          code,
          name,
          sendNotificationsWhenCreated,
          sendNotificationsWhenApproved,
          sendNotificationsWhenDisapproved,
          notificationEmails,
          allowJustificationWhenApprovingTheDocument,
        },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const update = async (
    id: number,
    {
      code,
      name,
      sendNotificationsWhenCreated,
      sendNotificationsWhenApproved,
      sendNotificationsWhenDisapproved,
      notificationEmails,
      validationOptionId,
      hoursOfLateness,
      validateLatenessFromBackoffice,
      allowJustificationWhenApprovingTheDocument,
      sendNotificationsEmployeeWhenArchived,
      sendEmailToManagerWhenDocumentApproved
    }: ICompany,
  ): Promise<ICompany> => {
    return await company
      .put({
        path: `${path}/${id}`,
        sendAuthorization: true,
        body: {
          code,
          name,
          sendNotificationsWhenCreated,
          sendNotificationsWhenApproved,
          sendNotificationsWhenDisapproved,
          notificationEmails,
          validationOptionId,
          hoursOfLateness,
          validateLatenessFromBackoffice,
          allowJustificationWhenApprovingTheDocument,
          sendNotificationsEmployeeWhenArchived,
          sendEmailToManagerWhenDocumentApproved
        },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const remove = async (id: number): Promise<void> => {
    return await company
      .remove({
        path: `${path}/${id}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  return { list, listCompanyByName, listByEmployeeId, findById, listCompanySearch, create, update, remove };
};

export { useCompanyService };
